import React from 'react'
import styled from 'styled-components'
import { FeatureWithInfoAndStyle } from './statsAndGeoToFeatureCollection.util'
import {
  childrenItemViewLabels,
  compatibleSpaceChildren,
  spaceTypePluralLabels,
  spaceTypeSingularLabels,
} from '~/packages/general/variable/spaceTime.data'
import { ButtonProperties, buttonCSS } from '~/prix/react/components/button'
import {
  Layer,
  SpaceChildrenItemView,
  SpaceGeoStandardId,
  SpaceGeoStandardType,
  SpaceType,
} from '~/packages/general/variable/spaceTime.types'
import { ReportWithLayer } from './reports'
import { formatAsBrIntegerNumber, formatAsBrNumber, formatExpressionValue } from '~/prix'
import { canUseCoverageChoropleth } from './extraLayerContainment.util'
import { isEmpty } from '~/prix/utils/empty'
import { isVariableLayer } from './clusterListInput.util'

const ButtonLink = styled.a<ButtonProperties>`
  ${buttonCSS}
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  margin-right: 1rem;
  margin-bottom: 0;
`

const Wrapper = styled.div`
  max-width: 550px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    padding-right: 30px;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.2rem;
  }

  .buttons {
    margin-top: 0.2rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  hr {
    margin: 0.5rem 0;
    border: 0.1px solid rgba(0, 0, 0, 0.1);
  }
`

export interface InfoWindowProps {
  formatValue?: (value: number) => string
  layers: Layer[]
  layer: Layer
  onChangeLayers: (layers: Layer[] | ((layers: Layer[]) => Layer[])) => void
  onChangeReport: (report: ReportWithLayer | null) => void
  feature: FeatureWithInfoAndStyle
  getLayerValueAt?: (
    layerIndex: number,
    geoId: string | number | undefined,
  ) => { value?: number; numerator?: number; denominator?: number } | null
  formatValueByIndex?: (layerIndex: number, value: number) => string
}

export default function InfoWindow({
  formatValue,
  layer,
  layers,
  onChangeLayers,
  onChangeReport,
  feature,
  getLayerValueAt,
  formatValueByIndex,
}: InfoWindowProps) {
  const { name, value, geoType, geoId, numerator, denominator } = feature.properties

  if (!isVariableLayer(layer)) {
    throw new Error('Info window requires a variable layer')
  }

  const variableLayers = layers.filter(isVariableLayer)
  if (variableLayers.length !== layers.length) {
    throw new Error('Info window requires only variable layers')
  }

  const compareButtonHideActions = [
    'kpiYearlyCumulativeStats',
  ]

  const currentCompatibleChildren = layer.variable.compatibleChildrenTypes
    ? compatibleSpaceChildren[geoType as keyof typeof compatibleSpaceChildren].filter(
        (type: SpaceType) => layer.variable.compatibleChildrenTypes!.includes(type),
      )
    : compatibleSpaceChildren[geoType as keyof typeof compatibleSpaceChildren]

  const handleChangeSpace =
    (childrenType: SpaceType, childrenItemView?: SpaceChildrenItemView) =>
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()

      onChangeLayers(
        layers.map(currentLayer => {
          if (layer !== currentLayer) {
            return currentLayer
          }
          if (childrenType === 'item') {
            if (!childrenItemView) {
              throw new Error('childrenItemView is required when childrenType is item')
            }

            return {
              ...currentLayer,
              space: {
                delimitationType: geoType as SpaceGeoStandardType,
                delimitationId: geoId as SpaceGeoStandardId,
                delimitationTitle: name,
                delimitationCustom: geoType === 'custom' ? feature : undefined,
                childrenType: 'item',
                childrenTitle:
                  childrenItemViewLabels[childrenItemView as keyof typeof childrenItemViewLabels],
                childrenItemView: childrenItemView as SpaceChildrenItemView,
              },
            }
          }

          return {
            ...currentLayer,
            space: {
              delimitationType: geoType as SpaceGeoStandardType,
              delimitationId: geoId as SpaceGeoStandardId,
              delimitationCustom: geoType === 'custom' ? feature.geometry : undefined,
              delimitationTitle: name,
              childrenType: childrenType as SpaceGeoStandardType,
              childrenTitle: spaceTypePluralLabels[childrenType],
            },
          }
        }),
      )
    }

  const handleCompare = () => {
    onChangeReport({
      layer,
      reportKey: 'selfComparison',
      reportPayload: {
        spaceDelimitationId: geoId,
        spaceDelimitationType: geoType,
        normalLabel: 'Geral',
        foundLabel: name,
      },
    })
  }

  const hasTwoLayers = layers.length === 2
  const useCoverageChoropleth = hasTwoLayers
    ? canUseCoverageChoropleth(variableLayers[0], variableLayers[1])
    : false

  const variable0 = hasTwoLayers
    ? getLayerValueAt?.(0, geoId)
    : { value: value, numerator, denominator }
  const variable1 = hasTwoLayers
    ? getLayerValueAt?.(1, geoId)
    : { value: value, numerator, denominator }

  const coveragePct =
    useCoverageChoropleth &&
    variable0?.value &&
    variable0.value !== 0 &&
    variable1?.value !== undefined
      ? (Number(variable1.value) / Number(variable0.value)) * 100
      : undefined

  // Helper para formatar valores das camadas (quando há duas)
  const formatLayerValue = (index: number, numericValue: number): string => {
    if (formatValueByIndex) {
      return formatValueByIndex(index, numericValue)
    }

    const variable = variableLayers[index].variable
    const isPercentage = variable.unitFormat === 'percentage'

    if (isPercentage) {
      return `${formatAsBrNumber(numericValue)}%`
    }

    return formatExpressionValue(Number(numericValue), variable, true)
  }

  return (
    <Wrapper>
      <h3>{name}</h3>
      {hasTwoLayers ? (
        <>
          {variable0?.value !== undefined && (
            <>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.9rem' }}>
                {variableLayers[0].variable.label
                  ? variableLayers[0].variable.label
                  : variableLayers[0].variable.title}
              </p>
              <p style={{ margin: '0 0 4px 0' }}>{formatLayerValue(0, Number(variable0.value))}</p>
            </>
          )}
          {variable1?.value !== undefined && (
            <>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.9rem' }}>
                {variableLayers[1].variable.label
                  ? variableLayers[1].variable.label
                  : variableLayers[1].variable.title}
              </p>
              <p style={{ margin: '0 0 4px 0' }}>{formatLayerValue(1, Number(variable1.value))}</p>
            </>
          )}
          {useCoverageChoropleth && coveragePct !== undefined && (
            <>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.9rem' }}>Cobertura</p>
              <p style={{ margin: '0 0 4px 0' }}>{`${formatAsBrNumber(coveragePct)}%`}</p>
            </>
          )}
        </>
      ) : typeof value === 'number' ? (
        <p>
          {(() => {
            const hasFraction = !isEmpty(numerator) && !isEmpty(denominator)
            const isPercentage = layer.variable.unitFormat === 'percentage'

            // Quando temos numerador / denominador, mostramos também a base de cálculo.
            if (hasFraction) {
              const base = `${formatAsBrIntegerNumber(Number(numerator))} de ${formatAsBrIntegerNumber(
                Number(denominator),
              )} registros`

              if (isPercentage) {
                const pctText = formatValue ? formatValue(value) : `${formatAsBrNumber(value)}%`
                return `${base} (${pctText})`
              }

              const inner = formatValue ? formatValue(value) : formatAsBrNumber(value)
              return `${base} (${inner})`
            }

            // Sem fração, mas também variável percentual (ex: IDS)
            if (isPercentage) {
              const pctText = formatAsBrNumber(value)
              return `${pctText}%`
            }

            // Demais casos, formatValue customizado
            if (formatValue) {
              return formatValue(value)
            }

            return formatExpressionValue(Number(value), layer.variable, true)
          })()}
        </p>
      ) : null}

      <div className='buttons'>
        {currentCompatibleChildren &&
          currentCompatibleChildren.map((childrenType: SpaceType) => {
            const isSameClickedAndCurrent = layer.space?.delimitationType === geoType
            const shouldHide = isSameClickedAndCurrent && childrenType === geoType

            if (childrenType !== 'item') {
              if (shouldHide) return null
              return (
                <ButtonLink onClick={handleChangeSpace(childrenType)} key={childrenType}>
                  {childrenType === geoType
                    ? spaceTypeSingularLabels[geoType]
                    : spaceTypePluralLabels[childrenType]}
                </ButtonLink>
              )
            }

            return (Object.keys(childrenItemViewLabels) as SpaceChildrenItemView[])
              .filter(itemType => itemType !== 'arc')
              .map(itemType => (
                <ButtonLink key={itemType} onClick={handleChangeSpace('item', itemType)}>
                  {childrenItemViewLabels[itemType as keyof typeof childrenItemViewLabels]}
                </ButtonLink>
              ))
          })}
      </div>
      {layer.space?.delimitationType !== geoType &&
        layer.space?.delimitationType !== 'custom' &&
        !compareButtonHideActions.includes(layer.variable.actionDefinitionKey ?? '') && (
          <React.Fragment>
            <hr />
            <div className='buttons'>
              <ButtonLink onClick={handleCompare} styleType='secondary'>
                Comparar
              </ButtonLink>
            </div>
          </React.Fragment>
        )}
    </Wrapper>
  )
}
